import axios from "axios";
import { settings } from "../settings/configuration";

import {
  main,
  configuration,
  STATUS_ONLINE_NOCONNECTION,
  STATUS_ONLINE,
} from "../main-context";

axios.interceptors.request.use((req) => {
  console.log(`${req.method} ${req.url}`);
  // Important: request interceptors **must** return the request.
  return req;
});

const instance = axios.create({
  headers: {
    "X-JetFuel-Offline": "true",
  },
});

export const getFromDbIndex = (
  url,
  resolve,
  update_context,
  regular_request = true
) => {
  const transaction = window.db.transaction(["requests"], "readonly");
  const objectStore = transaction.objectStore("requests");
  const request = objectStore.get(url);

  if (navigator.onLine && regular_request) {
    update_context.update_ui_context((prev) => ({
      ...prev,
      online_status: STATUS_ONLINE_NOCONNECTION,
    }));
  }
  request.onerror = function () {
    if (regular_request) {
      update_context.update_ui_context((prev) => ({
        ...prev,
        error: "No offline data - not possible to proceed!",
      }));
    }
    resolve("comm error");
  };
  request.onsuccess = function () {
    // Do something with the request.result!
    if (!request.result) {
      if (regular_request) {
        update_context.update_ui_context((prev) => ({
          ...prev,
          error: "No offline data - not possible to proceed!",
        }));
      }
      resolve("comm error");
    } else {
      const now = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      if (
        request.result.timestamp.getDate() === now.getDate() ||
        (request.result.timestamp.getDate() === yesterday.getDate() &&
          now.getHours() < 2)
      ) {
        resolve(request.result.data);
      } else {
        if (regular_request) {
          update_context.update_ui_context((prev) => ({
            ...prev,
            error: "Offline data too old!",
          }));
        }
        resolve("comm error");
      }
    }
  };
};

export const getSavedDataFromDbIndex = (update_context) => {
  return new Promise((resolve) => {
    const transaction = window.db.transaction(["saves"], "readonly");
    const objectStore = transaction.objectStore("saves");
    const request = objectStore.getAll();
    request.onerror = function () {
      update_context.update_ui_context((prev) => ({
        ...prev,
        error: "Error in reading saved data from database!",
      }));
      resolve("comm_error");
    };
    request.onsuccess = function () {
      // Do something with the request.result!
      if (request.result) {
        update_context.update_main_context((prev) => ({
          ...prev,
          saves: request.result,
        }));
      }
      resolve(request.result);
    };
  });
};

export const deleteSaveFromDatabase = (ident, update_context) => {
  return new Promise((resolve) => {
    const transaction = window.db.transaction(["saves"], "readwrite");
    const objectStore = transaction.objectStore("saves");
    const index = objectStore.index("ident");
    const request = index.get(ident);
    request.onerror = function () {
      resolve("comm_error");
    };
    request.onsuccess = function () {
      if (request.result && request.result.timestamp) {
        const innerRequest = objectStore.delete(request.result.timestamp);
        innerRequest.onerror = function () {
          resolve("comm_error");
        };
        innerRequest.onsuccess = function () {
          update_context.update_main_context((prev) => ({
            ...prev,
            saves: [...prev.saves.filter((x) => x.ident !== ident)],
          }));
          resolve(true);
        };
      } else {
        resolve(false);
      }
    };
  });
};

export const checkExistsInDatabase = (key) => {
  return new Promise((resolve) => {
    const transaction = window.db.transaction(["requests"], "readonly");
    const objectStore = transaction.objectStore("requests");
    const request = objectStore.get(key);
    request.onerror = function () {
      resolve("comm_error");
    };
    request.onsuccess = function () {
      if (
        request.result &&
        request.result.data &&
        request.result.data.errorHtml &&
        request.result.data.footer
      ) {
        const new_request = objectStore.get(key + "?partial=true");
        new_request.onerror = function () {
          resolve("comm_error");
        };
        new_request.onsuccess = function () {
          resolve(true);
        };
      } else if (request.result) {
        resolve(true);
      } else {
        resolve(false);
      }
    };
  });
};

export const clearDbIndex = () => {
  return new Promise((resolve, reject) => {
    const transaction = window.db.transaction(["requests"], "readwrite");
    const objectStore = transaction.objectStore("requests");
    const request = objectStore.getAll();
    const now = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    request.onsuccess = () => {
      if (request.result) {
        for (const data of request.result) {
          if (
            data.request_data.startsWith("/api/inspections") &&
            !data.request_data.startsWith("/api/inspections/fso") &&
            (data.timestamp.getDate() === now.getDate() ||
              (data.timestamp.getDate() === yesterday.getDate() &&
                now.getHours() < 2))
          ) {
            continue;
          }

          objectStore.delete(data.request_data);
        }
      }

      resolve();
    };
    request.onerror = () => reject(request.error);
  });
};

export const putToDbIndex = (url, id, data, clear = false) => {
  const transaction = window.db.transaction(["requests"], "readwrite");
  const objectStore = transaction.objectStore("requests");
  if (clear) {
    clearDbIndex()
      .then(() =>
        objectStore.put({
          request_data: url,
          id: id,
          data,
          timestamp: new Date(),
        })
      )
      .catch(() => console.log("Clear DB error"));
  } else {
    objectStore.put({
      request_data: url,
      id: id,
      data,
      timestamp: new Date(),
    });
  }
};

export const removeRequestFromDbIndex = (url) => {
  const transaction = window.db.transaction(["requests"], "readwrite");
  const objectStore = transaction.objectStore("requests");
  objectStore.delete(url);
};

function saveToDbIndex(url, id, data, update_context, ident, priority) {
  const transaction = window.db.transaction(["saves"], "readwrite");
  const objectStore = transaction.objectStore("saves");
  const save_data = {
    url: url,
    id: id,
    data,
    timestamp: new Date(),
    ident: ident,
    priority,
  };
  objectStore.put(save_data);
  update_context.update_main_context((prev) => ({
    ...prev,
    saves: [...prev.saves, save_data],
  }));
}

function deleteFromDbIndex(timestamp, update_context, resolve, data) {
  const transaction = window.db.transaction(["saves"], "readwrite");
  const objectStore = transaction.objectStore("saves");
  const request = objectStore.delete(timestamp);
  request.onsuccess = function () {
    update_context.update_main_context((prev) => ({
      ...prev,
      saves: [...prev.saves.filter((x) => x.timestamp !== timestamp)],
    }));
    resolve(data);
  };

  request.onerror = function () {
    resolve("comm error");
  };
}

export const login = (url, data, update_context) => {
  return new Promise((resolve) => {
    instance
      .post(configuration.settings.url + url, data, { withCredentials: true })
      .then((response) => {
        if (navigator.onLine) {
          update_context.update_ui_context((prev) => ({
            ...prev,
            online_status: STATUS_ONLINE,
          }));
        }
        putToDbIndex(url, response.data.id, response.data, true);
        resolve(response.data);
      })
      .catch((error) => {
        if (error.message === "Network Error") {
          getFromDbIndex(url, resolve, update_context);
        } else {
          processError(error, update_context, null, resolve, false, url, -1);
        }
      });
  });
};

export const postAndForget = (url, data, update_context) => {
  return new Promise((resolve) => {
    instance
      .post(configuration.settings.url + url, data, { withCredentials: true })
      .then((response) => {
        resolve([response.data, response]);
      })
      .catch((error) => {
        processError(error, update_context, null, resolve, false, url, -1);
      });
  });
};

export const getAndForget = (url, data, update_context) => {
  const params = { withCredentials: true, timeout: 1200000 };
  if (data && Object.keys(data).length) {
    url += "?" + new URLSearchParams(Object.entries(data)).toString();
  }
  return new Promise((resolve) => {
    instance
      .get(configuration.settings.url + url, params)
      .then((response) => {
        resolve([response.data, response]);
      })
      .catch((error) => {
        processError(error, update_context, null, resolve, false, url, -1);
      });
  });
};

export const get = (
  url,
  data,
  id,
  update_context,
  history,
  return_error_400 = false
) => {
  const params = { withCredentials: true, timeout: 1200000 };
  if (data && Object.keys(data).length) {
    url += "?" + new URLSearchParams(Object.entries(data)).toString();
  }
  if (settings.log_network) {
    console.log("GET", url);
  }
  return new Promise((resolve) => {
    if (navigator.onLine) {
      instance
        .get(configuration.settings.url + url, params)
        .then((response) => {
          if (navigator.onLine) {
            update_context.update_ui_context((prev) => ({
              ...prev,
              online_status: STATUS_ONLINE,
            }));
          }
          putToDbIndex(url, id, response.data);
          resolve(response.data);
        })
        .catch((error) => {
          if (error.message === "Network Error") {
            getFromDbIndex(url, resolve, update_context);
          } else {
            processError(
              error,
              update_context,
              history,
              resolve,
              return_error_400,
              url,
              id
            );
          }
        });
    } else {
      getFromDbIndex(url, resolve, update_context);
    }
  });
};

export const save_from_database = (url, data, timestamp, update_context) => {
  if (settings.log_network) {
    console.log("SAVE FROM DATABASE", url);
  }
  return new Promise((resolve) => {
    instance
      .post(configuration.settings.url + url, data, { withCredentials: true })
      .then((response) => {
        deleteFromDbIndex(timestamp, update_context, resolve, response.data);
      })
      .catch((error) => {
        if (error.message === "Network Error") {
          update_context.update_ui_context((prev) => ({
            ...prev,
            online_status: STATUS_ONLINE_NOCONNECTION,
          }));
          resolve("comm error");
        } else {
          console.log("ERROR DURING SAVING: " + error);
          deleteFromDbIndex(timestamp, update_context, resolve, "comm error");
        }
      });
  });
};

export const save = (
  url,
  data,
  id,
  update_context,
  history,
  ident,
  priority
) => {
  if (settings.log_network) {
    console.log(
      "SAVE",
      configuration.settings.url +
        url +
        (settings.xdebug_key
          ? url.indexOf("?") === -1
            ? "?XDEBUG_SESSION_START=PHPSTORM"
            : "&XDEBUG_SESSION_START=PHPSTORM"
          : "")
    );
  }
  return new Promise((resolve) => {
    if (navigator.onLine) {
      instance
        .post(
          configuration.settings.url +
            url +
            (settings.xdebug_key
              ? url.indexOf("?") === -1
                ? "?XDEBUG_SESSION_START=PHPSTORM"
                : "&XDEBUG_SESSION_START=PHPSTORM"
              : ""),
          data,
          { withCredentials: true }
        )
        .then((response) => {
          if (navigator.onLine) {
            update_context.update_ui_context((prev) => ({
              ...prev,
              online_status: STATUS_ONLINE,
            }));
          }
          resolve(response.data);
        })
        .catch((error) => {
          if (error.message === "Network Error") {
            saveToDbIndex(url, id, data, update_context, ident, priority);
            resolve(true);
          } else {
            processError(
              error,
              update_context,
              history,
              resolve,
              false,
              url,
              id
            );
          }
        });
    } else {
      saveToDbIndex(url, id, data, update_context, ident, priority);
      resolve(true);
    }
  });
};

function processError(
  error,
  update_context,
  history,
  resolve,
  return_error_400,
  url,
  id
) {
  if (
    error.response &&
    error.response.status === 401 &&
    update_context &&
    history
  ) {
    clearDbIndex()
      .then(() => {
        update_context.update_main_context(main);
        update_context.update_ui_context((prev) => ({
          ...prev,
          error: "Access not authorized",
        }));
        resolve("comm error");
        if (history) {
          history.push(update_context.settings.subfolder);
        }
      })
      .catch((error) => {
        update_context.update_ui_context((prev) => ({
          ...prev,
          error: error,
        }));
        resolve("comm error");
      });
  } else if (
    error.response &&
    return_error_400 === true &&
    error.response.status === 400 &&
    error.response.data.errorHtml
  ) {
    putToDbIndex(url, id, error.response.data);
    resolve(error.response.data);
  }  else {
    if (error.response && error.response.data) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.data.errorHtml) {
        const span = document.createElement("span");
        span.innerHTML = error.response.data.errorHtml;
        update_context.update_ui_context((prev) => ({
          ...prev,
          error: span.textContent,
        }));
      } else if (
        error.response.data.error &&
        typeof error.response.data.error === "string" &&
        error.response.data.error !== "true"
      ) {
        update_context.update_ui_context((prev) => ({
          ...prev,
          error: error.response.data.error,
        }));
      } else if (
        error.response.data.error &&
        error.response.data.error.message &&
        typeof error.response.data.error.message === "string"
      ) {
        update_context.update_ui_context((prev) => ({
          ...prev,
          error: error.response.data.error.message,
        }));
      } else if (
        error.response.data.message &&
        typeof error.response.data.message === "string"
      ) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        update_context.update_ui_context((prev) => ({
          ...prev,
          error: error.response.data.message,
        }));
      }
    } else if (error.response) {
      console.log(error.response);
      update_context.update_ui_context((prev) => ({
        ...prev,
        error: error.response.toString(),
      }));
    } else if (error.request) {
      console.log(error.request);
      update_context.update_ui_context((prev) => ({
        ...prev,
        error: error.message,
      }));
    } else {
      console.log("Error", error.message);
      update_context.update_ui_context((prev) => ({
        ...prev,
        error: error.message,
      }));
    }

    resolve("comm error");
  }
}
